import { createSlice } from '@reduxjs/toolkit';
import { HelpdeskSettingsState } from './types';
import {
  fetchHelpdeskSettings,
  updateHelpdeskSettings,
  fetchRoutingRules,
  createRoutingRule,
  updateRoutingRule,
  deleteRoutingRule,
  fetchSLAPolicies,
  createSLAPolicy,
  updateSLAPolicy,
  deleteSLAPolicy,
  fetchSchedules,
  updateSchedule,
  createSchedule,
} from './thunks';

const initialState: HelpdeskSettingsState = {
  settings: null,
  routingRules: [],
  slaPolicies: [],
  schedules: [],
  status: 'idle',
  rulesStatus: 'idle',
  slaStatus: 'idle',
  schedulesStatus: 'idle',
  error: null,
};

const helpdeskSettingsSlice = createSlice({
  name: 'helpdeskSettings',
  initialState,
  reducers: {
    clearHdSettingsError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // --- Settings singleton ---
    builder
      .addCase(fetchHelpdeskSettings.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchHelpdeskSettings.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.settings = action.payload;
      })
      .addCase(fetchHelpdeskSettings.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? 'Ошибка';
      });

    builder.addCase(updateHelpdeskSettings.fulfilled, (state, action) => {
      state.settings = action.payload;
    });

    // --- Routing Rules ---
    builder
      .addCase(fetchRoutingRules.pending, (state) => {
        state.rulesStatus = 'loading';
      })
      .addCase(fetchRoutingRules.fulfilled, (state, action) => {
        state.rulesStatus = 'succeeded';
        state.routingRules = action.payload;
      })
      .addCase(fetchRoutingRules.rejected, (state, action) => {
        state.rulesStatus = 'failed';
        state.error = action.payload ?? 'Ошибка';
      });

    builder.addCase(createRoutingRule.fulfilled, (state, action) => {
      state.routingRules.push(action.payload);
    });
    builder.addCase(updateRoutingRule.fulfilled, (state, action) => {
      const idx = state.routingRules.findIndex((r) => r.id === action.payload.id);
      if (idx !== -1) state.routingRules[idx] = action.payload;
    });
    builder.addCase(deleteRoutingRule.fulfilled, (state, action) => {
      state.routingRules = state.routingRules.filter((r) => r.id !== action.payload);
    });

    // --- SLA Policies ---
    builder
      .addCase(fetchSLAPolicies.pending, (state) => {
        state.slaStatus = 'loading';
      })
      .addCase(fetchSLAPolicies.fulfilled, (state, action) => {
        state.slaStatus = 'succeeded';
        state.slaPolicies = action.payload;
      })
      .addCase(fetchSLAPolicies.rejected, (state, action) => {
        state.slaStatus = 'failed';
        state.error = action.payload ?? 'Ошибка';
      });

    builder.addCase(createSLAPolicy.fulfilled, (state, action) => {
      state.slaPolicies.push(action.payload);
    });
    builder.addCase(updateSLAPolicy.fulfilled, (state, action) => {
      const idx = state.slaPolicies.findIndex((p) => p.id === action.payload.id);
      if (idx !== -1) state.slaPolicies[idx] = action.payload;
    });
    builder.addCase(deleteSLAPolicy.fulfilled, (state, action) => {
      state.slaPolicies = state.slaPolicies.filter((p) => p.id !== action.payload);
    });

    // --- Schedules ---
    builder
      .addCase(fetchSchedules.pending, (state) => {
        state.schedulesStatus = 'loading';
      })
      .addCase(fetchSchedules.fulfilled, (state, action) => {
        state.schedulesStatus = 'succeeded';
        state.schedules = action.payload;
      })
      .addCase(fetchSchedules.rejected, (state, action) => {
        state.schedulesStatus = 'failed';
        state.error = action.payload ?? 'Ошибка';
      });

    builder.addCase(updateSchedule.fulfilled, (state, action) => {
      const idx = state.schedules.findIndex((s) => s.id === action.payload.id);
      if (idx !== -1) state.schedules[idx] = action.payload;
    });
    builder.addCase(createSchedule.fulfilled, (state, action) => {
      state.schedules.push(action.payload);
    });
  },
});

export const { clearHdSettingsError } = helpdeskSettingsSlice.actions;
export default helpdeskSettingsSlice.reducer;
